import { deriveSeed, type SeedSource } from "./seed.js";

export const WORD_TENDING_SCHEMA_VERSION = 1 as const;
export const WORD_TENDING_TURNS = 6 as const;

export interface WordTarget {
  readonly line: number;
  readonly start: number;
  readonly end: number;
}

export interface WordDestination {
  readonly line: number;
  /** Code-point position after the selected word has been removed. */
  readonly position: number;
}

export interface KeepWordOperation {
  readonly kind: "keep";
  readonly target: WordTarget;
  readonly word: string;
}

export interface MoveWordOperation {
  readonly kind: "move";
  readonly target: WordTarget;
  readonly destination: WordDestination;
  readonly word: string;
}

export interface OfferWordOperation {
  readonly kind: "offer";
  readonly target: WordTarget;
  readonly toPlayer: number;
  readonly word: string;
}

export type WordTendingOperation =
  | KeepWordOperation
  | MoveWordOperation
  | OfferWordOperation;

export type WordTendingAction =
  | { readonly type: "reveal" }
  | { readonly type: "undo" }
  | { readonly type: "handoff" }
  | { readonly type: "keep"; readonly target: WordTarget }
  | {
    readonly type: "move";
    readonly target: WordTarget;
    readonly destination: WordDestination;
  }
  | {
    readonly type: "offer";
    readonly target: WordTarget;
    readonly toPlayer: number;
  };

export interface WordTendingPlayer {
  readonly index: number;
  readonly label: string;
}

export interface WordTendingHistoryEntry {
  readonly turn: number;
  readonly playerIndex: number;
  readonly playerLabel: string;
  readonly operation: WordTendingOperation;
}

export interface PendingWordOperation {
  readonly operation: WordTendingOperation;
  readonly beforeLines: readonly [string, string, string];
}

export type WordTendingPhase = "concealed" | "revealed" | "operated" | "complete";

export interface WordTendingState {
  readonly schemaVersion: typeof WORD_TENDING_SCHEMA_VERSION;
  readonly kind: "word-tending";
  readonly seed: SeedSource;
  readonly players: readonly string[];
  readonly originLines: readonly [string, string, string];
  readonly lines: readonly [string, string, string];
  readonly turn: number;
  readonly phase: WordTendingPhase;
  readonly activePlayer: WordTendingPlayer | null;
  readonly pendingOperation: PendingWordOperation | null;
  readonly history: readonly WordTendingHistoryEntry[];
  readonly summary: string | null;
}

export interface CreateWordTendingOptions {
  readonly seed: SeedSource;
  readonly players: readonly string[];
  readonly lines: readonly [string, string, string];
}

type DataRecord = ReadonlyMap<string, unknown>;

const STATE_KEYS = Object.freeze([
  "schemaVersion",
  "kind",
  "seed",
  "players",
  "originLines",
  "lines",
  "turn",
  "phase",
  "activePlayer",
  "pendingOperation",
  "history",
  "summary",
] as const);

const isWhitespace = (point: string): boolean => /\s/u.test(point);

const readRecord = (value: unknown, label: string): DataRecord => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new TypeError(`${label} must be a plain object`);
  }
  const prototype: unknown = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(`${label} must be a plain object`);
  }

  const values = new Map<string, unknown>();
  for (const key of Reflect.ownKeys(value)) {
    if (typeof key !== "string") throw new TypeError(`${label} must not contain symbol keys`);
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
      throw new TypeError(`${label}.${key} must be an enumerable data property`);
    }
    values.set(key, descriptor.value as unknown);
  }
  return values;
};

const readExact = (
  value: unknown,
  keys: readonly string[],
  label: string,
): readonly unknown[] => {
  const record = readRecord(value, label);
  if (record.size !== keys.length || keys.some((key) => !record.has(key))) {
    throw new TypeError(`${label} contains unknown or missing keys`);
  }
  return keys.map((key) => record.get(key));
};

const readArray = (value: unknown, label: string): readonly unknown[] => {
  if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype) {
    throw new TypeError(`${label} must be an array`);
  }
  const lengthDescriptor = Object.getOwnPropertyDescriptor(value, "length");
  if (
    lengthDescriptor === undefined
    || lengthDescriptor.enumerable
    || !("value" in lengthDescriptor)
    || !Number.isSafeInteger(lengthDescriptor.value)
    || lengthDescriptor.value < 0
  ) {
    throw new TypeError(`${label}.length must be a non-negative integer data property`);
  }
  const length = lengthDescriptor.value as number;
  if (Reflect.ownKeys(value).length !== length + 1) {
    throw new TypeError(`${label} must be dense and contain no extra properties`);
  }

  const result: unknown[] = [];
  for (let index = 0; index < length; index += 1) {
    const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
    if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
      throw new TypeError(`${label}[${index}] must be an enumerable data property`);
    }
    result.push(descriptor.value as unknown);
  }
  return result;
};

const readString = (value: unknown, label: string): string => {
  if (typeof value !== "string") throw new TypeError(`${label} must be a string`);
  return value;
};

const readInteger = (value: unknown, label: string, minimum: number, maximum: number): number => {
  if (!Number.isSafeInteger(value) || (value as number) < minimum || (value as number) > maximum) {
    throw new RangeError(`${label} must be an integer from ${minimum} through ${maximum}`);
  }
  return value as number;
};

const readSeed = (value: unknown): SeedSource => {
  if (typeof value === "string") return value;
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new TypeError("wordTending.seed must be a string or finite number");
  }
  return Object.is(value, -0) ? 0 : value;
};

const readPlayers = (value: unknown): readonly string[] => {
  const entries = readArray(value, "wordTending.players");
  if (entries.length < 2 || entries.length > 4) {
    throw new RangeError("wordTending.players must contain exactly 2 through 4 labels");
  }
  return Object.freeze(entries.map((entry, index) => {
    const player = readString(entry, `wordTending.players[${index}]`);
    if (player.length === 0 || player.trim().length === 0) {
      throw new TypeError(`wordTending.players[${index}] must be non-empty`);
    }
    return player;
  }));
};

const readLines = (value: unknown, label = "wordTending.lines"): readonly [string, string, string] => {
  const entries = readArray(value, label);
  if (entries.length !== 3) throw new RangeError(`${label} must contain exactly three lines`);
  return Object.freeze([
    readString(entries[0], `${label}[0]`),
    readString(entries[1], `${label}[1]`),
    readString(entries[2], `${label}[2]`),
  ]);
};

const freezeLines = (
  lines: readonly [string, string, string],
): readonly [string, string, string] => Object.freeze([lines[0], lines[1], lines[2]]);

const sameLines = (
  left: readonly [string, string, string],
  right: readonly [string, string, string],
): boolean => left.every((line, index) => line === right[index]);

const freezeTarget = (target: WordTarget): WordTarget => Object.freeze({ ...target });

const freezeDestination = (destination: WordDestination): WordDestination =>
  Object.freeze({ ...destination });

const freezeOperation = (operation: WordTendingOperation): WordTendingOperation => {
  if (operation.kind === "move") {
    return Object.freeze({
      kind: operation.kind,
      target: freezeTarget(operation.target),
      destination: freezeDestination(operation.destination),
      word: operation.word,
    });
  }
  if (operation.kind === "offer") {
    return Object.freeze({
      kind: operation.kind,
      target: freezeTarget(operation.target),
      toPlayer: operation.toPlayer,
      word: operation.word,
    });
  }
  return Object.freeze({
    kind: operation.kind,
    target: freezeTarget(operation.target),
    word: operation.word,
  });
};

const readTarget = (value: unknown, label: string): WordTarget => {
  const [line, start, end] = readExact(value, ["line", "start", "end"], label);
  const target = freezeTarget({
    line: readInteger(line, `${label}.line`, 0, 2),
    start: readInteger(start, `${label}.start`, 0, Number.MAX_SAFE_INTEGER),
    end: readInteger(end, `${label}.end`, 0, Number.MAX_SAFE_INTEGER),
  });
  if (target.start >= target.end) throw new RangeError(`${label} must be a non-empty range`);
  return target;
};

const readDestination = (value: unknown, label: string): WordDestination => {
  const [line, position] = readExact(value, ["line", "position"], label);
  return freezeDestination({
    line: readInteger(line, `${label}.line`, 0, 2),
    position: readInteger(position, `${label}.position`, 0, Number.MAX_SAFE_INTEGER),
  });
};

const selectedWord = (lines: readonly string[], target: WordTarget): string => {
  const points = Array.from(lines[target.line]);
  if (target.start >= target.end || target.end > points.length) {
    throw new RangeError("word target is outside its line");
  }
  const selected = points.slice(target.start, target.end);
  if (
    selected.some(isWhitespace)
    || (target.start > 0 && !isWhitespace(points[target.start - 1]))
    || (target.end < points.length && !isWhitespace(points[target.end]))
  ) {
    throw new RangeError("word target must select exactly one whitespace-delimited word");
  }
  return selected.join("");
};

const moveWord = (
  lines: readonly [string, string, string],
  target: WordTarget,
  destination: WordDestination,
): { readonly lines: readonly [string, string, string]; readonly word: string } => {
  const word = selectedWord(lines, target);
  const result = [...lines] as [string, string, string];
  const sourcePoints = Array.from(result[target.line]);
  result[target.line] = [
    ...sourcePoints.slice(0, target.start),
    ...sourcePoints.slice(target.end),
  ].join("");

  const destinationPoints = Array.from(result[destination.line]);
  if (destination.position > destinationPoints.length) {
    throw new RangeError("word destination is outside its line");
  }
  const left = destinationPoints[destination.position - 1];
  const right = destinationPoints[destination.position];
  if (left !== undefined && right !== undefined && !isWhitespace(left) && !isWhitespace(right)) {
    throw new RangeError("word destination must be at a word boundary");
  }
  const insertion = [
    ...(left !== undefined && !isWhitespace(left) ? [" "] : []),
    ...Array.from(word),
    ...(right !== undefined && !isWhitespace(right) ? [" "] : []),
  ];
  result[destination.line] = [
    ...destinationPoints.slice(0, destination.position),
    ...insertion,
    ...destinationPoints.slice(destination.position),
  ].join("");
  return { lines: Object.freeze(result), word };
};

const playerOrder = (seed: SeedSource, count: number): readonly number[] => {
  const order = Array.from({ length: count }, (_, index) => index);
  let value = deriveSeed("game/word-tending/v1", seed);
  const next = (): number => {
    if (value === 0) value = 0x9e3779b9;
    value ^= value << 13;
    value ^= value >>> 17;
    value ^= value << 5;
    value >>>= 0;
    return value;
  };
  for (let index = order.length - 1; index > 0; index -= 1) {
    const swapIndex = next() % (index + 1);
    [order[index], order[swapIndex]] = [order[swapIndex], order[index]];
  }
  return Object.freeze(order);
};

const playerForTurn = (seed: SeedSource, playerCount: number, turn: number): number =>
  playerOrder(seed, playerCount)[turn % playerCount];

const completionSummary = (history: readonly WordTendingHistoryEntry[]): string => {
  const count = (kind: WordTendingOperation["kind"]): number =>
    history.filter((entry) => entry.operation.kind === kind).length;
  return `Word Tending complete after 6 turns: ${count("keep")} keep, ${count("move")} move, ${count("offer")} offer; 3 lines remain.`;
};

const readStoredOperation = (
  value: unknown,
  playerCount: number,
  activeIndex: number,
  lines?: readonly [string, string, string],
): WordTendingOperation => {
  const record = readRecord(value, "wordTending.operation");
  const kind = record.get("kind");
  let operation: WordTendingOperation;
  if (kind === "keep") {
    const [, targetValue, wordValue] = readExact(
      value,
      ["kind", "target", "word"],
      "wordTending.operation",
    );
    operation = freezeOperation({
      kind,
      target: readTarget(targetValue, "wordTending.operation.target"),
      word: readString(wordValue, "wordTending.operation.word"),
    });
  } else if (kind === "move") {
    const [, targetValue, destinationValue, wordValue] = readExact(
      value,
      ["kind", "target", "destination", "word"],
      "wordTending.operation",
    );
    operation = freezeOperation({
      kind,
      target: readTarget(targetValue, "wordTending.operation.target"),
      destination: readDestination(destinationValue, "wordTending.operation.destination"),
      word: readString(wordValue, "wordTending.operation.word"),
    });
  } else if (kind === "offer") {
    const [, targetValue, toPlayerValue, wordValue] = readExact(
      value,
      ["kind", "target", "toPlayer", "word"],
      "wordTending.operation",
    );
    const toPlayer = readInteger(
      toPlayerValue,
      "wordTending.operation.toPlayer",
      0,
      playerCount - 1,
    );
    if (toPlayer === activeIndex) throw new RangeError("a player cannot offer a word to themself");
    operation = freezeOperation({
      kind,
      target: readTarget(targetValue, "wordTending.operation.target"),
      toPlayer,
      word: readString(wordValue, "wordTending.operation.word"),
    });
  } else {
    throw new TypeError("wordTending.operation.kind is unknown");
  }
  if (operation.word.length === 0 || Array.from(operation.word).some(isWhitespace)) {
    throw new TypeError("wordTending.operation.word must be one non-empty word");
  }
  if (operation.target.end - operation.target.start !== Array.from(operation.word).length) {
    throw new TypeError("wordTending operation target does not match its word length");
  }
  if (lines !== undefined) {
    const exactWord = selectedWord(lines, operation.target);
    if (operation.word !== exactWord) throw new TypeError("stored operation word does not match");
  }
  return operation;
};

const freezeState = (state: WordTendingState): WordTendingState => {
  const players = Object.freeze([...state.players]);
  const originLines = freezeLines(state.originLines);
  const lines = freezeLines(state.lines);
  const activePlayer = state.activePlayer === null
    ? null
    : Object.freeze({ ...state.activePlayer });
  const pendingOperation: PendingWordOperation | null = state.pendingOperation === null
    ? null
    : Object.freeze({
      operation: freezeOperation(state.pendingOperation.operation),
      beforeLines: freezeLines(state.pendingOperation.beforeLines),
    });
  const history = Object.freeze(state.history.map((entry) => Object.freeze({
    turn: entry.turn,
    playerIndex: entry.playerIndex,
    playerLabel: entry.playerLabel,
    operation: freezeOperation(entry.operation),
  })));
  return Object.freeze({
    schemaVersion: WORD_TENDING_SCHEMA_VERSION,
    kind: "word-tending" as const,
    seed: state.seed,
    players,
    originLines,
    lines,
    turn: state.turn,
    phase: state.phase,
    activePlayer,
    pendingOperation,
    history,
    summary: state.summary,
  });
};

const readState = (value: unknown): WordTendingState => {
  const [
    schemaVersion,
    kind,
    seedValue,
    playersValue,
    originLinesValue,
    linesValue,
    turnValue,
    phaseValue,
    activePlayerValue,
    pendingValue,
    historyValue,
    summaryValue,
  ] = readExact(value, STATE_KEYS, "wordTending");
  if (schemaVersion !== WORD_TENDING_SCHEMA_VERSION || kind !== "word-tending") {
    throw new TypeError("wordTending version or kind is unknown");
  }
  const seed = readSeed(seedValue);
  const players = readPlayers(playersValue);
  const originLines = readLines(originLinesValue, "wordTending.originLines");
  const lines = readLines(linesValue);
  const turn = readInteger(turnValue, "wordTending.turn", 0, WORD_TENDING_TURNS);
  if (
    phaseValue !== "concealed"
    && phaseValue !== "revealed"
    && phaseValue !== "operated"
    && phaseValue !== "complete"
  ) {
    throw new TypeError("wordTending.phase is unknown");
  }
  const phase = phaseValue;
  const historyValues = readArray(historyValue, "wordTending.history");
  if (historyValues.length !== turn) {
    throw new TypeError("wordTending history must contain one entry per completed turn");
  }
  let replayedLines = originLines;
  const history = Object.freeze(historyValues.map((entryValue, index) => {
    const [entryTurn, playerIndexValue, playerLabelValue, operationValue] = readExact(
      entryValue,
      ["turn", "playerIndex", "playerLabel", "operation"],
      `wordTending.history[${index}]`,
    );
    if (entryTurn !== index) throw new TypeError("wordTending history turn is out of order");
    const expectedIndex = playerForTurn(seed, players.length, index);
    const playerIndex = readInteger(
      playerIndexValue,
      `wordTending.history[${index}].playerIndex`,
      0,
      players.length - 1,
    );
    if (playerIndex !== expectedIndex || playerLabelValue !== players[playerIndex]) {
      throw new TypeError("wordTending history player does not match seeded order");
    }
    const operation = readStoredOperation(
      operationValue,
      players.length,
      playerIndex,
      replayedLines,
    );
    if (operation.kind === "move") {
      replayedLines = moveWord(replayedLines, operation.target, operation.destination).lines;
    }
    return Object.freeze({
      turn: index,
      playerIndex,
      playerLabel: readString(playerLabelValue, `wordTending.history[${index}].playerLabel`),
      operation,
    });
  }));

  const expectedActiveIndex = turn < WORD_TENDING_TURNS
    ? playerForTurn(seed, players.length, turn)
    : null;
  let activePlayer: WordTendingPlayer | null = null;
  if (activePlayerValue !== null) {
    const [indexValue, labelValue] = readExact(
      activePlayerValue,
      ["index", "label"],
      "wordTending.activePlayer",
    );
    const index = readInteger(indexValue, "wordTending.activePlayer.index", 0, players.length - 1);
    const label = readString(labelValue, "wordTending.activePlayer.label");
    if (index !== expectedActiveIndex || label !== players[index]) {
      throw new TypeError("wordTending.activePlayer does not match seeded order");
    }
    activePlayer = Object.freeze({ index, label });
  }

  let pendingOperation: PendingWordOperation | null = null;
  let reachableLines = replayedLines;
  if (pendingValue !== null) {
    if (expectedActiveIndex === null) throw new TypeError("completed game cannot have an operation");
    const [operationValue, beforeLinesValue] = readExact(
      pendingValue,
      ["operation", "beforeLines"],
      "wordTending.pendingOperation",
    );
    const beforeLines = readLines(beforeLinesValue, "wordTending.pendingOperation.beforeLines");
    if (!sameLines(beforeLines, replayedLines)) {
      throw new TypeError("wordTending pending operation does not begin at replayed lines");
    }
    const operation = readStoredOperation(
      operationValue,
      players.length,
      expectedActiveIndex,
      replayedLines,
    );
    reachableLines = operation.kind === "move"
      ? moveWord(replayedLines, operation.target, operation.destination).lines
      : replayedLines;
    pendingOperation = Object.freeze({ operation, beforeLines });
  }

  if (!sameLines(lines, reachableLines)) {
    throw new TypeError("wordTending current lines do not match replayed operations");
  }

  if (phase === "concealed" && (turn >= WORD_TENDING_TURNS || activePlayer !== null || pendingOperation !== null)) {
    throw new TypeError("concealed wordTending state is inconsistent");
  }
  if (phase === "revealed" && (activePlayer === null || pendingOperation !== null)) {
    throw new TypeError("revealed wordTending state is inconsistent");
  }
  if (phase === "operated" && (activePlayer === null || pendingOperation === null)) {
    throw new TypeError("operated wordTending state is inconsistent");
  }
  if (phase === "complete" && (turn !== WORD_TENDING_TURNS || activePlayer !== null || pendingOperation !== null)) {
    throw new TypeError("complete wordTending state is inconsistent");
  }
  const expectedSummary = phase === "complete" ? completionSummary(history) : null;
  if (summaryValue !== expectedSummary) throw new TypeError("wordTending summary is inconsistent");

  return freezeState({
    schemaVersion: WORD_TENDING_SCHEMA_VERSION,
    kind: "word-tending",
    seed,
    players,
    originLines,
    lines,
    turn,
    phase,
    activePlayer,
    pendingOperation,
    history,
    summary: expectedSummary,
  });
};

const readOperationAction = (
  action: unknown,
  state: WordTendingState,
): { readonly operation: WordTendingOperation; readonly lines: readonly [string, string, string] } => {
  const record = readRecord(action, "wordTending.action");
  const type = record.get("type");
  if (state.activePlayer === null) throw new TypeError("wordTending operation requires reveal");

  if (type === "keep") {
    const [, targetValue] = readExact(action, ["type", "target"], "wordTending.action");
    const target = readTarget(targetValue, "wordTending.action.target");
    const word = selectedWord(state.lines, target);
    return {
      operation: freezeOperation({ kind: "keep", target, word }),
      lines: state.lines,
    };
  }
  if (type === "move") {
    const [, targetValue, destinationValue] = readExact(
      action,
      ["type", "target", "destination"],
      "wordTending.action",
    );
    const target = readTarget(targetValue, "wordTending.action.target");
    const destination = readDestination(destinationValue, "wordTending.action.destination");
    const moved = moveWord(state.lines, target, destination);
    return {
      operation: freezeOperation({ kind: "move", target, destination, word: moved.word }),
      lines: moved.lines,
    };
  }
  if (type === "offer") {
    const [, targetValue, toPlayerValue] = readExact(
      action,
      ["type", "target", "toPlayer"],
      "wordTending.action",
    );
    const target = readTarget(targetValue, "wordTending.action.target");
    const toPlayer = readInteger(
      toPlayerValue,
      "wordTending.action.toPlayer",
      0,
      state.players.length - 1,
    );
    if (toPlayer === state.activePlayer.index) {
      throw new RangeError("a player cannot offer a word to themself");
    }
    return {
      operation: freezeOperation({
        kind: "offer",
        target,
        toPlayer,
        word: selectedWord(state.lines, target),
      }),
      lines: state.lines,
    };
  }
  throw new TypeError("wordTending action is not a word operation");
};

export function createWordTendingGame(options: unknown): WordTendingState {
  const [seedValue, playersValue, linesValue] = readExact(
    options,
    ["seed", "players", "lines"],
    "wordTending options",
  );
  const lines = readLines(linesValue);
  return freezeState({
    schemaVersion: WORD_TENDING_SCHEMA_VERSION,
    kind: "word-tending",
    seed: readSeed(seedValue),
    players: readPlayers(playersValue),
    originLines: lines,
    lines,
    turn: 0,
    phase: "concealed",
    activePlayer: null,
    pendingOperation: null,
    history: Object.freeze([]),
    summary: null,
  });
}

export function reduceWordTending(stateValue: unknown, action: unknown): WordTendingState {
  const state = readState(stateValue);
  const actionRecord = readRecord(action, "wordTending.action");
  const type = actionRecord.get("type");

  if (state.phase === "complete") throw new RangeError("Word Tending is already complete");
  if (type === "reveal") {
    readExact(action, ["type"], "wordTending.action");
    if (state.phase !== "concealed") throw new TypeError("only a concealed turn can be revealed");
    const index = playerForTurn(state.seed, state.players.length, state.turn);
    return freezeState({
      ...state,
      phase: "revealed",
      activePlayer: Object.freeze({ index, label: state.players[index] }),
    });
  }
  if (type === "undo") {
    readExact(action, ["type"], "wordTending.action");
    if (state.phase !== "operated" || state.pendingOperation === null) {
      throw new TypeError("only the current unconfirmed operation can be undone");
    }
    return freezeState({
      ...state,
      lines: state.pendingOperation.beforeLines,
      phase: "revealed",
      pendingOperation: null,
    });
  }
  if (type === "handoff") {
    readExact(action, ["type"], "wordTending.action");
    if (
      state.phase !== "operated"
      || state.pendingOperation === null
      || state.activePlayer === null
    ) {
      throw new TypeError("handoff requires exactly one current operation");
    }
    const history = Object.freeze([
      ...state.history,
      Object.freeze({
        turn: state.turn,
        playerIndex: state.activePlayer.index,
        playerLabel: state.activePlayer.label,
        operation: freezeOperation(state.pendingOperation.operation),
      }),
    ]);
    const turn = state.turn + 1;
    const complete = turn === WORD_TENDING_TURNS;
    return freezeState({
      ...state,
      turn,
      phase: complete ? "complete" : "concealed",
      activePlayer: null,
      pendingOperation: null,
      history,
      summary: complete ? completionSummary(history) : null,
    });
  }
  if (state.phase !== "revealed") {
    throw new TypeError("a word operation requires the revealed current turn");
  }
  const result = readOperationAction(action, state);
  return freezeState({
    ...state,
    lines: result.lines,
    phase: "operated",
    pendingOperation: Object.freeze({
      operation: result.operation,
      beforeLines: state.lines,
    }),
  });
}
